// Performance utilities for optimizing animations and interactions

// Throttle function using requestAnimationFrame
export const rafThrottle = <T extends (...args: any[]) => void>(func: T): T & { cancel: () => void } => {
  let rafId: number | null = null;
  let lastArgs: Parameters<T> | null = null;

  const throttled = ((...args: Parameters<T>) => {
    lastArgs = args;
    
    if (rafId === null) {
      rafId = requestAnimationFrame(() => {
        if (lastArgs) {
          func(...lastArgs);
        }
        rafId = null;
        lastArgs = null;
      });
    }
  }) as T & { cancel: () => void };

  // Cleanup function
  throttled.cancel = () => {
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
      lastArgs = null;
    }
  };

  return throttled;
};

// Debounce function for resize events
export const debounce = <T extends (...args: any[]) => void>(
  func: T,
  delay: number
): T & { cancel: () => void } => {
  let timeoutId: NodeJS.Timeout | null = null;

  const debounced = ((...args: Parameters<T>) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => func(...args), delay);
  }) as T & { cancel: () => void };

  debounced.cancel = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  return debounced;
};

// GPU acceleration helper
export const enableGPUAcceleration = (element: HTMLElement) => {
  element.style.willChange = 'transform';
  element.style.transform = 'translateZ(0)';
  element.style.backfaceVisibility = 'hidden';
  element.style.perspective = '1000px';
};

export const disableGPUAcceleration = (element: HTMLElement) => {
  element.style.willChange = 'auto';
  element.style.transform = '';
  element.style.backfaceVisibility = '';
  element.style.perspective = '';
};

// Intersection Observer for performance
export const createPerformanceObserver = (
  callback: (entries: IntersectionObserverEntry[]) => void,
  options: IntersectionObserverInit = {}
): IntersectionObserver => {
  const defaultOptions: IntersectionObserverInit = {
    rootMargin: '50px',
    threshold: 0.1,
    ...options,
  };

  return new IntersectionObserver(callback, defaultOptions);
};

// Optimize transforms for better performance
export const getOptimizedTransform = (
  translateX: number = 0,
  translateY: number = 0,
  rotate: number = 0,
  scale: number = 1
): string => {
  // Use translate3d for GPU acceleration
  return `translate3d(${translateX}px, ${translateY}px, 0) rotate(${rotate}deg) scale(${scale})`;
};

// Check if device supports hardware acceleration
export const supportsHardwareAcceleration = (): boolean => {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    return !!gl;
  } catch (e) {
    return false;
  }
};

// Memory and performance monitoring
export const getPerformanceMetrics = () => {
  const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
  
  return {
    // Memory info (if available)
    memory: (performance as any).memory ? {
      used: (performance as any).memory.usedJSHeapSize,
      total: (performance as any).memory.totalJSHeapSize,
      limit: (performance as any).memory.jsHeapSizeLimit,
    } : null,
    
    // Connection info
    connection: (navigator as any).connection ? {
      effectiveType: (navigator as any).connection.effectiveType,
      downlink: (navigator as any).connection.downlink,
      rtt: (navigator as any).connection.rtt,
    } : null,
    
    // Timing info
    timing: navigation ? {
      domContentLoaded: navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart,
      loadComplete: navigation.loadEventEnd - navigation.loadEventStart,
      domProcessing: navigation.domComplete - navigation.responseStart,
    } : null,
  };
};

// Animation frame scheduler for heavy operations
export class AnimationScheduler {
  private tasks: (() => void)[] = [];
  private isRunning = false;

  add(task: () => void) {
    this.tasks.push(task);
    this.schedule();
  }

  private schedule() {
    if (this.isRunning || this.tasks.length === 0) return;
    
    this.isRunning = true;
    requestAnimationFrame(() => {
      const startTime = performance.now();
      const maxTime = 16; // Keep under 16ms for 60fps
      
      while (this.tasks.length > 0 && (performance.now() - startTime) < maxTime) {
        const task = this.tasks.shift();
        if (task) task();
      }
      
      this.isRunning = false;
      if (this.tasks.length > 0) {
        this.schedule();
      }
    });
  }

  clear() {
    this.tasks = [];
  }
}

// Singleton instance
export const animationScheduler = new AnimationScheduler();

// Preload images with intersection observer
export const preloadImagesOnVisible = (imageUrls: string[], threshold = 0.1) => {
  const images = new Map<string, HTMLImageElement>();
  
  const observer = createPerformanceObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        const target = entry.target as HTMLElement;
        const imageUrl = target.dataset.preloadSrc;
        
        if (imageUrl && !images.has(imageUrl)) {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.src = imageUrl;
          images.set(imageUrl, img);
        }
        
        observer.unobserve(target);
      }
    });
  }, { threshold });

  return {
    observer,
    images,
    cleanup: () => observer.disconnect(),
  };
};

// CSS containment utility
export const applyCSSContainment = (element: HTMLElement, containment: string = 'layout style paint') => {
  element.style.contain = containment;
};

// Passive event listener helper
export const addPassiveEventListener = <T extends Event>(
  element: Element | Window | Document,
  event: string,
  handler: (event: T) => void,
  options: AddEventListenerOptions = {}
) => {
  const passiveOptions = { passive: true, ...options };
  const eventHandler = handler as EventListener;
  element.addEventListener(event, eventHandler, passiveOptions);
  
  return () => element.removeEventListener(event, eventHandler, passiveOptions);
};